import React from 'react';
import {
    Button,
    Col,
    Container,
    Progress,
    Row,
} from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAddressCard,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { Can } from '../Can';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    TextFilterDefaults,
    LinkCellRenderer,
    ButtonCellRenderer,
    getServerSideRowRequest,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    PageHeading,
    PageWrap,
    toasty,
} from '../common/forms/FormElements';
import { Modal } from '../common/Modal';
import { DatePicker } from '../common/forms/DatePicker';

class ProjectIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
            selectedRow: null,
            isCalculating: false,
        };
    }

    componentDidMount() {
        return this.populateState();
    }

    onDownloadAccrualReport = async (values) => {
        const { gridOptions } = this.state;

        const formatYMD = (date) => new Date(date).toISOString().split('T')[0];

        const req = getServerSideRowRequest(gridOptions);
        const filteredData = await httpClient.post(ApiRoutes.project.search(), req);
        if (!filteredData?.rows) {
            toasty.error('No projects found for the selected filters. Please adjust your search criteria and try again.');
            return;
        }
        const param = { ...values, projectIds: filteredData?.rows?.map((p) => p.id) ?? [] };

        await httpClient.downloadFile(
            ApiRoutes.report.accrualReport(),
            param,
            `Rental Accrual Report ${formatYMD(values.startDate)}_${formatYMD(values.endDate)}.xlsx`,
        );
    };

    calculateRentalUnbilledRevenue = async () => {
        const { gridOptions } = this.state;
        this.setState({ isCalculating: true });
        try {
            const req = getServerSideRowRequest(gridOptions);
            const filteredData = await httpClient.post(ApiRoutes.project.search(), req);
            if (!filteredData?.rows) {
                toasty.error('No projects found for the selected filters. Please adjust your search criteria and try again.');
                return;
            }

            const response = await httpClient.post(
                ApiRoutes.project.calculateRentalUnbilledRevenue(),
                filteredData?.rows?.map((p) => p.id) ?? [],
                httpClient.format.none);

            if (response.ok) {
                toasty.success('The rental unbilled revenue has been successfully recalculated.');
            } else {
                toasty.error('Failed to recalculate rental unbilled revenue. Please try again later.');
            }
            gridOptions.refresh();
        } finally {
            this.setState({ isCalculating: false });
        }
    };

    async populateState() {
        const { search } = { ...this.props.location };

        const contractNumber = search ? new URLSearchParams(search).get('n') ?? '' : '';

        const [dispatchLocations] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
        ]);
        const { user } = this.context;
        // Admins can select all dispatch, others default to their dispatch loc.
        let initialDispatchValue = 0;
        if (!user.isAdmin) initialDispatchValue = user.dispatchCompanyId;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialDispatchValue,
        };

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            statusFilter: DataGridSelectFilter,
            statusFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            buttonRenderer: ButtonCellRenderer,
        };

        const yesNo = [
            { label: 'Closed', value: 'true' },
            { label: 'Open', value: 'false' },
        ];

        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        const quickRentalFilterParams = {
            suppressFilterButton: true,
            options: [
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
            ],
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        const isRentalContractFilterParams = {
            suppressFilterButton: true,
            options: [
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
            ],
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
                flex: 0,
                maxWidth: 80,
                minWidth: 80,
            },
            {
                colId: 'ContractSequence',
                headerName: 'Project Number',
                field: 'projectNumber',
                sortable: true,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    to: (id) => `${AppNavPaths.Project}/${id}`,
                    nameField: 'projectNumber',
                    idField: 'id',
                    title: 'View this Project',
                },
            },
            {
                colId: 'Contract.Company.CompanyName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Contract.Number',
                headerName: 'Contract Number',
                field: 'contractNumber',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'WorkOrderNumber',
                headerName: 'WO #',
                field: 'workOrderNumber',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchCompany.CompanyName',
                headerName: 'Dispatching',
                sortable: true,
                field: 'dispatchOfficeLocation',
                tooltipField: 'dispatchOfficeLocation',
                filter: 'statusFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'statusFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'JobLocation.Address1',
                headerName: 'Address',
                field: 'address',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobLocation.City',
                headerName: 'City',
                field: 'city',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'LastInvoiced',
                headerName: 'Last Invoiced Date',
                field: 'lastInvoiced',
                sortable: true,
                filter: false,
                valueFormatter: (params) => params.value ? moment(params.value).format('MM/DD/YYYY') : '',
            },
            {
                colId: 'RentalStartDate',
                headerName: 'Rental Start Date',
                field: 'rentalStartDate',
                sortable: false,
                filter: false,
                valueFormatter: (params) => params.value ? moment(params.value).format('MM/DD/YYYY') : '',
            },
            {
                colId: 'RentalEndDate',
                headerName: 'Rental End Date',
                field: 'rentalEndDate',
                sortable: false,
                filter: false,
                valueFormatter: (params) => params.value ? moment(params.value).format('MM/DD/YYYY') : '',
            },
            {
                colId: 'EquipmentRemaining',
                headerName: 'Equipment On Site',
                field: 'equipmentRemaining',
                width: 150,
                sortable: false,
                filter: false,
                valueFormatter: (params) => params.value || '0',
            },
            {
                colId: 'UnbilledRentalRevenue',
                headerName: 'Unbilled Rental Revenue',
                field: 'unbilledRentalRevenue',
                width: 150,
                sortable: false,
                filter: false,
                valueFormatter: (params) => params.value ? parseFloat(params.value).toFixed(2) : '0.00',
            },
            {
                colId: 'IsClosed',
                headerName: 'Status',
                field: 'isClosed',
                width: 120,
                sortable: true,
                filter: 'statusFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'statusFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
                valueFormatter: (params) => params.value ? 'Closed' : 'Open',
            },
            {
                colId: 'HasQuickRentalJob',
                headerName: 'Quick Rental',
                field: 'hasQuickRentalJob',
                width: 130,
                sortable: false,
                filter: 'statusFilter',
                floatingFilter: true,
                filterParams: quickRentalFilterParams,
                floatingFilterComponent: 'statusFloatingFilter',
                floatingFilterComponentParams: quickRentalFilterParams,
                valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            },
            {
                colId: 'Contract.IsProjectContract',
                headerName: 'Is Rental',
                field: 'isRentalContract',
                width: 120,
                sortable: false,
                filter: 'statusFilter',
                floatingFilter: true,
                filterParams: isRentalContractFilterParams,
                floatingFilterComponent: 'statusFloatingFilter',
                floatingFilterComponentParams: isRentalContractFilterParams,
                valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            },
        ];

        gridOptions.onGridReady = (params) => {
            // default filter values

            if (this.state.preFilters?.contractNumber) {
                const filterComponent = params.api.getFilterInstance('Contract.Number');
                filterComponent.setModel({
                    filter: contractNumber,
                    filterType: 'text',
                    key: 'Contract.Number',
                    type: 'contains',
                });

                params.api.onFilterChanged();
            }

            gridOptions.api = params.api;
            this.setState({ gridApi: params.api });

            params.api.setDatasource(this.state.dataSource, gridOptions);
        };

        const preFilters = {
            contractNumber,
        };

        const dataSource = createDataSource(ApiRoutes.project.search(), gridOptions);
        this.setState({
            loading: false, gridOptions, dataSource, preFilters,
        });
    }

    getContractIdParam = () => {
        const { search } = { ...this.props.location };
        return search ? new URLSearchParams(search).get('i') ?? null : null;
    };

    showAccrualReportModal = () => {
        Modal.show({
            title: 'Rental Accrual Report',
            onSubmit: (values) => this.onDownloadAccrualReport(values),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: ({ values }) => (
                <Container>
                    <Row>
                        <Col xs={6}>
                            <DatePicker
                                id="startDate"
                                label="Start Date"
                                name="startDate"
                                selectsStart
                                startDate={values.startDate}
                                endDate={values.endDate}
                                showTimeInput={false}
                                required
                            />
                        </Col>

                        <Col xs={6}>
                            <DatePicker
                                id="endDate"
                                name="endDate"
                                label="End Date"
                                selectsEnd
                                startDate={values.startDate}
                                endDate={values.endDate}
                                minDate={values.startDate}
                                showTimeInput={false}
                                required
                            />
                        </Col>
                    </Row>
                </Container>
            ),
        });
    };

    render() {
        if (this.state.loading) return (<Progress />);

        const {
            rowData,
            gridOptions,
            isCalculating,
        } = this.state;

        const contractId = this.getContractIdParam();

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faAddressCard}
                        className="mr-2 text-muted"
                    />
                    <span>Projects</span>
                    <Can
                        I="view"
                        a="invoice"
                    >
                        <span
                            style={{
                                float: 'right',
                                position: 'relative',
                                top: '-5px',
                            }}
                        >
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                onClick={this.showAccrualReportModal}
                            >
                                <FontAwesomeIcon
                                    icon={faDownload}
                                    className="mr-2"
                                    size="sm"
                                />
                                Export Accrual Report
                            </Button>
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                disabled={isCalculating}
                                onClick={this.calculateRentalUnbilledRevenue}
                            >
                                Recalculate Unbilled Revenue
                            </Button>
                        </span>
                    </Can>
                </PageHeading>
                <DataGridToolbar
                    entity="job"
                    gridApi={this.state.gridApi}
                    gridOptions={this.state.gridOptions}
                    dataSource={this.state.dataSource}
                    hideAdd={!contractId}
                    onAdd={() => this.context.history.push(`${AppNavPaths.ProjectNew}/${contractId}`)}
                    addLabel="Add Project"
                    hideExcelButton
                    serverExport={{ apiPath: ApiRoutes.project.excelExport(), filePrefix: 'ProjectSearch' }}
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}
export default ProjectIndex;
